import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MembershipBadge } from "@/components/MembershipBadge";
import { MediaUploader, MediaPreview } from "@/components/MediaUploader";
import { Send, MessageSquare, ArrowLeft, Mic, Plus } from "lucide-react";
import { toast } from "sonner";

function Avatar({ url, name, size = 36 }) {
    const initial = (name || "?").charAt(0).toUpperCase();
    if (url) {
        const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        return <img src={full} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.42 }}>
            {initial}
        </div>
    );
}

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s/60)}m ago`;
    if (s < 86400) return `${Math.floor(s/3600)}h ago`;
    if (s < 604800) return `${Math.floor(s/86400)}d ago`;
    return new Date(iso).toLocaleDateString();
}

// Tracks whether the viewport is < 768px (Tailwind's `md` breakpoint).
// Used to render the Instagram-style composer on mobile only — desktop
// keeps its current 3-line composer untouched.
function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.matchMedia("(max-width: 767px)").matches;
    });
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(max-width: 767px)");
        const handler = (e) => setIsMobile(e.matches);
        // Safari < 14 uses addListener
        if (mq.addEventListener) mq.addEventListener("change", handler);
        else mq.addListener(handler);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", handler);
            else mq.removeListener(handler);
        };
    }, []);
    return isMobile;
}

// Compact rounded-square Project Unveiled logomark — 36px button used as the
// left affordance of the mobile composer.
function ComposerLogoButton() {
    return (
        <div
            className="flex items-center justify-center rounded-xl flex-shrink-0"
            style={{
                width: 36,
                height: 36,
                background: "var(--ok-surface)",
                border: "1px solid var(--ok-border)",
            }}
            aria-hidden
            data-testid="composer-logo-btn"
        >
            <svg width="22" height="22" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <mask id="composerLogoMask">
                        <rect width="64" height="64" fill="white" />
                        <polygon points="34,2 62,2 62,14 44,14" fill="black" />
                    </mask>
                </defs>
                <g mask="url(#composerLogoMask)">
                    <circle cx="32" cy="32" r="24" fill="none" stroke="var(--ok-accent)" strokeWidth="7" strokeLinecap="round" />
                </g>
            </svg>
        </div>
    );
}

export default function Messages() {
    const { user, loading: authLoading } = useAuth();
    const { peerId } = useParams();
    const navigate = useNavigate();
    const [threads, setThreads] = useState([]);
    const [loadingThreads, setLoadingThreads] = useState(true);
    const [following, setFollowing] = useState([]);

    useEffect(() => {
        if (!authLoading && !user) navigate("/login");
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (user) {
            api.get("/messages/threads")
                .then((r) => setThreads(r.data?.items || []))
                .finally(() => setLoadingThreads(false));
            api.get(`/social/users/${user.id}/following`)
                .then((r) => setFollowing(r.data?.items || []))
                .catch(() => {});
        }
    }, [user]);

    if (!user) return null;

    return (
        <div className="ok-container py-8 max-w-5xl" data-testid="messages-page">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2">Private Channel · UNVEILED</div>
            <h1 className="font-display text-3xl mb-6">Messages</h1>
            <div className="grid md:grid-cols-[320px,1fr] gap-5 min-h-[60vh]">
                <aside className="ok-card p-3" data-testid="messages-thread-list">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mb-2 px-2">Conversations</div>
                    {loadingThreads ? (
                        <div className="text-xs font-mono opacity-50 p-3">Loading…</div>
                    ) : threads.length === 0 ? (
                        <div className="text-xs opacity-70 p-3">
                            No conversations yet. Open another witness&apos;s profile to start a message.
                        </div>
                    ) : (
                        <ul className="space-y-1">
                            {threads.map((t) => (
                                <li key={t.id}>
                                    <Link
                                        to={`/messages/${t.peer.id}`}
                                        className={`block px-2 py-2 rounded hover:bg-[var(--ok-surface-2)] transition ${peerId === t.peer.id ? "bg-[var(--ok-surface-2)]" : ""}`}
                                        data-testid={`thread-${t.peer.id}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Avatar url={t.peer.avatar_url} name={t.peer.name} size={32} />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 text-sm truncate">
                                                    <span className="truncate">{t.peer.name || "Witness"}</span>
                                                    {t.peer.subscription_tier === "research_council" && <MembershipBadge tier="research_council" size="sm" />}
                                                    {t.peer.subscription_tier === "verified_witness" && <MembershipBadge tier="verified_witness" size="sm" />}
                                                </div>
                                                <div className="text-xs opacity-60 truncate">{t.last_message_body || ""}</div>
                                            </div>
                                            {t.unread > 0 && (
                                                <span className="text-[10px] font-mono bg-[var(--ok-accent)] text-black px-1.5 rounded-full" data-testid={`thread-unread-${t.peer.id}`}>{t.unread}</span>
                                            )}
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                    {following.length > 0 && (
                        <div className="mt-4" data-testid="messages-following-list">
                            <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mb-2 px-2">People you follow</div>
                            <ul className="space-y-1">
                                {following.slice(0, 12).map((f) => (
                                    <li key={f.id}>
                                        <Link
                                            to={`/messages/${f.id}`}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--ok-surface-2)] transition"
                                            data-testid={`message-following-${f.id}`}
                                        >
                                            <Avatar url={f.avatar_url} name={f.name} size={26} />
                                            <span className="text-sm truncate flex-1">{f.name || "Witness"}</span>
                                            {f.subscription_tier === "research_council" && <MembershipBadge tier="research_council" size="sm" />}
                                            {f.subscription_tier === "verified_witness" && <MembershipBadge tier="verified_witness" size="sm" />}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </aside>

                <section className="ok-card p-0 flex flex-col min-w-0" data-testid="messages-conversation">
                    {peerId ? (
                        <Conversation peerId={peerId} onSent={(t) => setThreads(t)} />
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center p-10 text-center opacity-70">
                            <MessageSquare className="w-10 h-10 opacity-50 mb-3" />
                            <p className="text-sm">Select a conversation, or visit another witness&apos;s profile to start one.</p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

function Conversation({ peerId, onSent }) {
    const { user } = useAuth();
    const [thread, setThread] = useState(null); // { peer, messages }
    const [loading, setLoading] = useState(true);
    const [body, setBody] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const isMobile = useIsMobile();

    // Desktop composer: starts at 3 lines, grows downward. (Unchanged behaviour.)
    const DESKTOP_LINE_PX = 22;
    const DESKTOP_PADDING_PX = 16;
    const DESKTOP_MIN_LINES = 3;
    const DESKTOP_MIN_H = DESKTOP_LINE_PX * DESKTOP_MIN_LINES + DESKTOP_PADDING_PX;

    // Mobile composer: Instagram-style. Starts at exactly one line, grows
    // upward to a sensible max (~8 lines), then internally scrolls.
    const MOBILE_LINE_PX = 22;
    const MOBILE_PADDING_PX = 16;
    const MOBILE_MIN_LINES = 1;
    const MOBILE_MAX_LINES = 8;
    const MOBILE_MIN_H = MOBILE_LINE_PX * MOBILE_MIN_LINES + MOBILE_PADDING_PX;
    const MOBILE_MAX_H = MOBILE_LINE_PX * MOBILE_MAX_LINES + MOBILE_PADDING_PX;

    const autoGrow = () => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        if (isMobile) {
            const next = Math.min(MOBILE_MAX_H, Math.max(MOBILE_MIN_H, el.scrollHeight));
            el.style.height = `${next}px`;
            // Internal scroll only kicks in once we hit the mobile max.
            el.style.overflowY = el.scrollHeight > MOBILE_MAX_H ? "auto" : "hidden";
        } else {
            const next = Math.max(DESKTOP_MIN_H, el.scrollHeight);
            el.style.height = `${next}px`;
            el.style.overflowY = "hidden";
        }
    };

    // Reset textarea height whenever the viewport flips between mobile/desktop,
    // so a hot reload during testing doesn't leave a stale inline height.
    useEffect(() => {
        autoGrow();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, body]);

    useEffect(() => {
        if (peerId) {
            api.get(`/messages/threads/with/${peerId}`)
                .then((r) => setThread({ peer: r.data?.peer || null, messages: r.data?.messages || [] }))
                .finally(() => setLoading(false));
        }
    }, [peerId]);

    const peer = thread?.peer;
    const msgs = thread?.messages || [];

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [msgs]);

    async function send() {
        if (!body.trim() && !attachment) return;
        setSending(true);
        try {
            const payload = { to_user_id: peerId, body: body.trim() };
            if (attachment) payload.media_id = attachment.id;
            const { data } = await api.post("/messages", payload);
            setThread((cur) => cur ? { ...cur, messages: [...(cur.messages || []), data] } : cur);
            setBody("");
            setAttachment(null);
            if (inputRef.current) {
                inputRef.current.style.height = `${isMobile ? MOBILE_MIN_H : DESKTOP_MIN_H}px`;
            }
            const r = await api.get("/messages/threads");
            onSent && onSent(r.data?.items || []);
        } catch (e) {
            toast.error("Send failed");
        } finally {
            setSending(false);
        }
    }

    if (loading) return <div className="p-6 text-xs font-mono opacity-50">Loading conversation…</div>;
    if (!peer) return <div className="p-6 text-sm opacity-70">Conversation unavailable.</div>;

    const hasText = body.trim().length > 0;

    return (
        <>
            <header className="border-b border-[var(--ok-border)] p-4 flex items-center gap-3">
                <Link to="/messages" className="md:hidden ok-btn-outline text-xs px-2"><ArrowLeft className="w-3 h-3" /></Link>
                <Avatar url={peer.avatar_url} name={peer.name} size={40} />
                <div className="flex-1">
                    <Link to={`/u/${peer.id}`} className="font-display text-lg hover:text-[var(--ok-accent)] flex items-center gap-2" data-testid="conversation-peer-name">
                        {peer.name || "Witness"}
                        {peer.subscription_tier === "research_council" && <MembershipBadge tier="research_council" size="sm" />}
                        {peer.subscription_tier === "verified_witness" && <MembershipBadge tier="verified_witness" size="sm" />}
                    </Link>
                </div>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[55vh]" data-testid="conversation-messages">
                {msgs.length === 0 && (
                    <div className="text-center text-xs opacity-50 py-10">No messages yet. Send the first one.</div>
                )}
                {msgs.map((m) => {
                    const mine = m.from_user_id === user.id;
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[var(--ok-accent)] text-black" : "ok-card"}`}>
                                {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                                {m.media && <div className="mt-1.5"><MediaPreview media={m.media} /></div>}
                                <div className={`mt-1 text-[10px] opacity-60 font-mono ${mine ? "" : ""}`}>{timeAgo(m.created_at)}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {isMobile ? (
                /* === MOBILE COMPOSER === Instagram-style: pill bubble, icon-only,
                   starts 1 line, grows to 8 lines max, then internally scrolls.
                   Active only on viewports < 768px. */
                <footer className="border-t border-[var(--ok-border)] p-2.5 safe-area-inset-bottom" data-testid="composer-mobile">
                    {attachment && (
                        <div className="mb-2">
                            <MediaPreview media={attachment} onRemove={() => setAttachment(null)} />
                        </div>
                    )}
                    <div className="flex items-end gap-2 min-w-0 w-full">
                        <ComposerLogoButton />
                        <div
                            className="flex items-end gap-1 min-w-0 flex-1"
                            style={{
                                background: "var(--ok-surface)",
                                border: "1px solid var(--ok-border)",
                                borderRadius: 22,
                                padding: "4px 6px 4px 14px",
                            }}
                        >
                            <textarea
                                ref={inputRef}
                                value={body}
                                onChange={(e) => { setBody(e.target.value); autoGrow(); }}
                                rows={1}
                                placeholder="Message…"
                                aria-label="Message"
                                className="flex-1 min-w-0 w-full text-sm resize-none leading-relaxed bg-transparent border-0 outline-none placeholder:opacity-50"
                                style={{
                                    minHeight: `${MOBILE_MIN_H}px`,
                                    maxHeight: `${MOBILE_MAX_H}px`,
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                    whiteSpace: "pre-wrap",
                                    padding: "8px 2px",
                                    color: "var(--ok-text)",
                                }}
                                data-testid="message-input"
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            />
                            {hasText ? (
                                <button
                                    onClick={send}
                                    disabled={sending}
                                    aria-label="Send"
                                    className="inline-flex items-center justify-center rounded-full flex-shrink-0 disabled:opacity-50 transition"
                                    style={{
                                        width: 36, height: 36,
                                        background: "var(--ok-accent)",
                                        color: "var(--ok-on-accent)",
                                    }}
                                    data-testid="message-send"
                                >
                                    <Send className="w-4 h-4" strokeWidth={2} />
                                </button>
                            ) : (
                                <div className="flex items-end flex-shrink-0">
                                    <button
                                        type="button"
                                        aria-label="Voice message (coming soon)"
                                        onClick={() => toast("Voice messages coming soon")}
                                        className="inline-flex items-center justify-center text-[var(--ok-text-muted)] hover:text-[var(--ok-accent)] transition"
                                        style={{ width: 36, height: 36 }}
                                        data-testid="message-mic"
                                    >
                                        <Mic className="w-5 h-5" strokeWidth={1.75} />
                                    </button>
                                    <MediaUploader iconOnly onUploaded={setAttachment} testId="message-attach" />
                                    <button
                                        type="button"
                                        aria-label="More options"
                                        onClick={() => toast("More options coming soon")}
                                        className="inline-flex items-center justify-center text-[var(--ok-text-muted)] hover:text-[var(--ok-accent)] transition"
                                        style={{ width: 36, height: 36 }}
                                        data-testid="message-more"
                                    >
                                        <Plus className="w-5 h-5" strokeWidth={1.75} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </footer>
            ) : (
                /* === DESKTOP COMPOSER === Unchanged from the previous fix:
                   3-line default, full panel width, grows downward. */
                <footer className="border-t border-[var(--ok-border)] p-3" data-testid="composer-desktop">
                    {attachment && (
                        <div className="mb-2">
                            <MediaPreview media={attachment} onRemove={() => setAttachment(null)} />
                        </div>
                    )}
                    <div className="flex items-end gap-2 min-w-0">
                        <textarea
                            ref={inputRef}
                            value={body}
                            onChange={(e) => { setBody(e.target.value); autoGrow(); }}
                            rows={DESKTOP_MIN_LINES}
                            placeholder="Send a private message…"
                            className="ok-input flex-1 min-w-0 w-full text-sm resize-none leading-relaxed overflow-hidden"
                            style={{ minHeight: `${DESKTOP_MIN_H}px`, wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
                            data-testid="message-input"
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        />
                        <MediaUploader compact onUploaded={setAttachment} testId="message-attach" />
                        <button onClick={send} disabled={sending || (!body.trim() && !attachment)} className="ok-btn-primary text-sm flex items-center gap-1 shrink-0" data-testid="message-send">
                            <Send className="w-3 h-3" /> Send
                        </button>
                    </div>
                </footer>
            )}
        </>
    );
}
